// Group slugs that would collide with a static route segment and make the
// group unreachable at its own URL. A Circle or Network named "New" derives
// the slug `new`, whose canonical URL `/circles/new` is already the
// create-a-Circle form — the router resolves the static route, not the group
// (issue #382). The single writer for group slugs (`insertGroupWithSlug`)
// treats these as taken and disambiguates them (`new` -> `new-2`), exactly
// as it does a cross-group uniqueness collision.
//
// There are two independent sources of shadowing, both reserved here:
//   - Web client routes under /circles and /networks (`new`, `create`): the
//     browser router resolves these to the create/new form, not the group.
//   - API static-sibling endpoints under /circles and /networks
//     (`locations` = `GET /circles/locations`, `apply` = `/networks/apply`):
//     the vanity-slug detail mount skips these segments
//     (slugResolvingDetailMw's staticSiblings), so a group slugged with one
//     never resolves. Same bug class, API side.
//
// Why a hand-declared constant here rather than a value derived from the
// route tree: the reserved words ARE those static route segments, but they
// live in the web app's route tree and the server's route mounts — the
// server writer that needs them cannot import the web route tree across the
// app boundary, and `@leanin/shared` is the one package both apps already
// depend on. So the set is canonical here, and two drift-guard tests keep it
// honest so it cannot silently fall out of sync when a route is added:
//   - apps/web/reserved-slugs.test.ts asserts it covers every web static
//     first-segment route under /circles and /networks.
//   - apps/server groups drift test asserts it covers every API
//     static-sibling segment (CIRCLE/NETWORK_SLUG_STATIC_SIBLINGS).
export const RESERVED_GROUP_SLUGS = new Set([
    'new', // web: /circles/new, /networks/new
    'create', // web: /networks/create
    'locations', // api: GET /circles/locations
    'apply', // api: /networks/apply
]);
export function isReservedSlug(slug) {
    return RESERVED_GROUP_SLUGS.has(slug);
}
// Literal path segments mounted under `/members/`, the member analogue of
// RESERVED_GROUP_SLUGS. Once `/members/{ref}` accepts a handle, any of these
// words used as a handle addresses a route instead of a member: the static
// mount wins and that member's profile is unreachable. The worst is
// `/members/me`, which returns the *viewer's* profile — a member holding the
// handle `me` would have shown every visitor themselves.
//
// These are URL segments, not handles — the two use different alphabets. A
// handle is stored dot-separated (`count.by.scope`) and addressed
// hyphen-separated (`count-by-scope`), so a candidate handle has to be
// converted with `handleToUrlSegment` before it is checked against this set.
// Comparing a raw handle here silently misses every multi-word route: no
// handle can ever equal `count-by-scope`, but `count.by.scope` maps onto it.
//
// Enforcement points, all required:
//   - `isMemberRef` refuses them, so the server param schema and the web
//     route's `beforeLoad` both treat `/members/facets` as addressing no
//     member (a 422 and a 404 respectively) rather than fetching the facets
//     endpoint and rendering its payload as a profile.
//   - `pickAvailableHandle` never mints a handle whose URL form is here.
//   - `handleResolvingDetailMw` skips them. Hono runs every `use` whose
//     pattern matches, and these words match the handle-shaped mount, so
//     without the skip list `/members/me` would be looked up as a handle
//     and 404 before the self-profile route ever ran.
//   - Migration 0197 rewrites any row that already mapped onto one.
//
// Drift guards: `apps/server/src/identity/__unit__/reserved-segments.test.ts`
// walks the mounted API route table, and `apps/web/reserved-slugs.test.ts`
// walks the web route tree, so a literal added under `/members/` on either
// side fails a test until it is listed here.
export const RESERVED_MEMBER_SEGMENTS = new Set([
    'count-by-scope',
    'facets',
    'invites',
    'me',
    'online',
    'profiles',
]);
// Case-insensitive on purpose: Hono matches literals case-sensitively, so
// `/members/ME` is not the self-profile route, but it cannot be a member
// either — the handle `me` is never minted and never survives migration
// 0197. A segment that is a reserved word in any case addresses nothing.
export function isReservedMemberSegment(segment) {
    return RESERVED_MEMBER_SEGMENTS.has(segment.toLowerCase());
}
//# sourceMappingURL=reserved-slugs.js.map